/**
 * SEC-009: XSS Prevention with DOMPurify
 * Industry-standard HTML sanitization for safe rendering
 *
 * Uses DOMPurify library for robust XSS protection
 *
 * CVSS 6.5 (MEDIUM) → 1.5 (LOW)
 * CWE-79: Cross-Site Scripting Prevention
 */

import DOMPurify from 'dompurify';
import { logger } from '@/utils/logger';

/**
 * Sanitization configuration profiles
 */
export const SANITIZE_PROFILES = {
  /**
   * STRICT: Only basic formatting tags allowed
   * Use for user-generated content with high security requirements
   */
  STRICT: {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'p', 'br', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: [],
    ALLOW_DATA_ATTR: false,
    KEEP_CONTENT: true,
  },

  /**
   * BASIC: Common formatting + links
   * Use for subsidy descriptions, eligibility criteria
   */
  BASIC: {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'u', 'p', 'br', 'ul', 'ol', 'li', 'a', 'span', 'div'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
    ALLOW_DATA_ATTR: false,
    KEEP_CONTENT: true,
  },

  /**
   * EXTENDED: Full formatting + headings + code
   * Use for help articles, documentation
   */
  EXTENDED: {
    ALLOWED_TAGS: [
      'b', 'i', 'em', 'strong', 'u', 'p', 'br', 'ul', 'ol', 'li', 'a', 'span', 'div',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'code', 'pre', 'blockquote',
      'table', 'thead', 'tbody', 'tr', 'th', 'td'
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'id'],
    ALLOW_DATA_ATTR: false,
    KEEP_CONTENT: true,
  },
} as const;

export type SanitizeProfile = keyof typeof SANITIZE_PROFILES;

/**
 * Sanitize HTML content using DOMPurify
 *
 * @param html - Raw HTML string
 * @param profile - Sanitization profile (default: 'BASIC')
 * @param customConfig - Optional DOMPurify config overrides
 * @returns Sanitized HTML string safe for rendering
 *
 * @example
 * ```typescript
 * const clean = sanitizeHTML('<script>alert("XSS")</script><p>Hello</p>');
 * // Returns: '<p>Hello</p>'
 *
 * const strict = sanitizeHTML('<a href="javascript:alert()">Click</a>', 'STRICT');
 * // Returns: 'Click' (link removed)
 * ```
 */
export function sanitizeHTML(
  html: string | null | undefined,
  profile: SanitizeProfile = 'BASIC',
  customConfig?: DOMPurify.Config
): string {
  // Handle null/undefined gracefully
  if (!html) return '';

  // Get base config from profile
  const baseConfig = SANITIZE_PROFILES[profile];

  // Merge with custom config
  const config: DOMPurify.Config = {
    ...baseConfig,
    ...customConfig,
    // Force secure defaults
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'style', 'form', 'input', 'button', 'select', 'textarea'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
    // Remove javascript: and data: URIs
    FORBID_PROTOCOLS: ['javascript', 'data', 'vbscript'],
    // Sanitize attributes
    SANITIZE_DOM: true,
    // Keep safe HTML entities
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
  };

  // Sanitize with DOMPurify
  const sanitized = DOMPurify.sanitize(html, config);

  // Additional post-processing for extra safety
  return sanitized
    .replace(/javascript:/gi, '') // Extra protection against javascript: protocol
    .replace(/on\w+\s*=/gi, '') // Remove any remaining event handlers
    .trim();
}

/**
 * Create safe HTML object for React's dangerouslySetInnerHTML
 *
 * @param html - Raw HTML string
 * @param profile - Sanitization profile (default: 'BASIC')
 * @returns Object with __html property containing sanitized HTML
 *
 * @example
 * ```tsx
 * <div dangerouslySetInnerHTML={createSafeHTML(subsidy.description)} />
 * ```
 */
export function createSafeHTML(
  html: string | null | undefined,
  profile: SanitizeProfile = 'BASIC'
): { __html: string } {
  return { __html: sanitizeHTML(html, profile) };
}

/**
 * Sanitize URL for href attributes
 * Only allows http:, https:, mailto:, tel:, and relative URLs
 *
 * @param url - URL string
 * @returns Sanitized URL or '#' if invalid
 *
 * @example
 * ```typescript
 * sanitizeURL('javascript:alert(1)') // Returns: '#'
 * sanitizeURL('https://example.com') // Returns: 'https://example.com'
 * sanitizeURL('/relative/path') // Returns: '/relative/path'
 * ```
 */
export function sanitizeURL(url: string | null | undefined): string {
  if (!url) return '#';

  const trimmed = url.trim();

  // Allow relative URLs
  if (trimmed.startsWith('/') || trimmed.startsWith('#')) {
    return trimmed;
  }

  // Allow safe protocols
  const safeProtocols = /^(https?:|mailto:|tel:)/i;
  if (safeProtocols.test(trimmed)) {
    return trimmed;
  }

  // Block everything else (including javascript:, data:, vbscript:)
  logger.warn(`[SEC-009] Blocked unsafe URL: ${url}`);
  return '#';
}

/**
 * Sanitize plain text for display
 * Removes all HTML tags and dangerous characters
 *
 * @param text - Plain text input
 * @returns Sanitized text safe for display
 *
 * @example
 * ```typescript
 * sanitizeText('<script>alert(1)</script>Hello')
 * // Returns: 'Hello'
 * ```
 */
export function sanitizeText(text: string | null | undefined): string {
  if (!text) return '';

  return text
    .replace(/[<>]/g, '') // Remove angle brackets
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/vbscript:/gi, '') // Remove vbscript: protocol
    .replace(/on\w+\s*=/gi, '') // Remove event handlers
    .replace(/data:/gi, '') // Remove data: protocol
    .trim();
}

/**
 * Validate if HTML content contains potentially dangerous elements
 * Use this to warn users or log security events
 *
 * @param html - HTML string to validate
 * @returns True if dangerous content detected
 */
export function containsDangerousContent(html: string | null | undefined): boolean {
  if (!html) return false;

  const dangerousPatterns = [
    /<script\b/i,
    /<iframe\b/i,
    /<object\b/i,
    /<embed\b/i,
    /javascript:/i,
    /vbscript:/i,
    /data:text\/html/i,
    /on\w+\s*=/i, // Event handlers
    /<form\b/i,
    /<input\b/i,
  ];

  return dangerousPatterns.some(pattern => pattern.test(html));
}

/**
 * Get sanitization statistics for monitoring
 * Compares original HTML to sanitized HTML
 *
 * @param original - Original HTML
 * @param sanitized - Sanitized HTML
 * @returns Statistics object
 */
export function getSanitizationStats(original: string, sanitized: string): {
  originalLength: number;
  sanitizedLength: number;
  bytesRemoved: number;
  percentRemoved: number;
  hadDangerousContent: boolean;
} {
  const originalLength = original.length;
  const sanitizedLength = sanitized.length;
  const bytesRemoved = originalLength - sanitizedLength;
  const percentRemoved = originalLength > 0 ? (bytesRemoved / originalLength) * 100 : 0;

  return {
    originalLength,
    sanitizedLength,
    bytesRemoved,
    percentRemoved,
    hadDangerousContent: containsDangerousContent(original) && bytesRemoved > 0,
  };
}

/**
 * Batch sanitize multiple HTML strings
 * Useful for sanitizing arrays of subsidies, articles, etc.
 *
 * @param htmlArray - Array of HTML strings
 * @param profile - Sanitization profile
 * @returns Array of sanitized HTML strings
 */
export function sanitizeHTMLBatch(
  htmlArray: (string | null | undefined)[],
  profile: SanitizeProfile = 'BASIC'
): string[] {
  return htmlArray.map(html => sanitizeHTML(html, profile));
}

/**
 * Configure DOMPurify hooks for custom processing
 * Call this once at app initialization
 */
export function initializeDOMPurify(): void {
  // Hook to log sanitization events
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    // Ensure all links open in new tab with noopener noreferrer
    if (node.tagName === 'A') {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });

  // Hook to log removed elements (for debugging) - rate-limited to avoid spam
  if (process.env.NODE_ENV === 'development') {
    const loggedTags = new Set<string>();
    DOMPurify.addHook('uponSanitizeElement', (node, data) => {
      if (data.allowedTags && !data.allowedTags[data.tagName]) {
        // Only log each tag type once per session to avoid console spam
        if (!loggedTags.has(data.tagName)) {
          loggedTags.add(data.tagName);
          logger.warn(`[SEC-009] Removed dangerous tag: ${data.tagName} (further occurrences suppressed)`);
        }
      }
    });
  }
}

// Export DOMPurify instance for advanced usage
export { DOMPurify };
